import { createFilter } from 'redux-persist-transform-filter'
import { ActionType, createReducer } from 'typesafe-actions'

import * as Actions from './actions'
import { ResourcesState, ResourcesAvailable } from './types'

export const FILTER_PERSISTOR = createFilter(
  'resources',
  null,
  [
    'billing_types',
    'contracts',
    'cost_centers',
    'currencies',
    'financial_resources',
    'payment_resources',
    'reasons',
    'references',
    'services',
    'targets'
  ],
  'whitelist'
)

export const PAGE_SIZE_EDIT = 30
export const PAGE_SIZE_SEARCH = 15

const INITIAL_STATE: ResourcesState = {
  billing_types: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  contracts: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  cost_centers: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  currencies: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  financial_resources: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  payment_resources: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  reasons: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  references: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  services: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  },
  targets: {
    allowAll: false,
    error: false,
    lastUpdate: null,
    list: [],
    loading: false,
    organization_id: -1,
    page: 1,
    total_pages: 0,
    total_results: 0
  }
}

function findKeyIndex(resource: keyof ResourcesAvailable) {
  switch (resource) {
    case 'currencies':
      return 'currency_id'
    default:
      return resource.replace(/s$/, '_id')
  }
}

const reducer = createReducer<ResourcesState, ActionType<typeof Actions>>(
  INITIAL_STATE
)
  .handleAction(Actions.addResource, (state, { payload }) => ({
    ...state,
    [payload.resource]: {
      ...state[payload.resource],
      list: [...state[payload.resource].list, { ...payload.item }]
    }
  }))
  .handleAction(Actions.finishLoader, (state, { payload }) => ({
    ...state,
    [payload.resource]: {
      ...state[payload.resource],
      loading: false
    }
  }))
  .handleAction(Actions.getResources, (state, { payload }) => ({
    ...state,
    [payload.resource]: {
      ...state[payload.resource],
      error: false,
      loading: true
    }
  }))
  .handleAction(Actions.getResourcesFailure, (state, { payload }) => ({
    ...state,
    [payload.resource]: {
      ...state[payload.resource],
      loading: false
    }
  }))
  .handleAction(Actions.getResourcesSuccess, (state, { payload }) => ({
    ...state,
    [payload.resource]: {
      ...state[payload.resource],
      allowAll: payload.allowAll,
      list: payload.list,
      loading: false,
      organization_id: payload.organization_id,
      page: payload.page,
      total_results: payload.total_results
    }
  }))
  .handleAction(Actions.reloadResourcesPageFailure, (state, { payload }) => ({
    ...state,
    [payload.resource]: {
      ...state[payload.resource],
      page: payload.page
    }
  }))
  .handleAction(Actions.setResources, (state, { payload }) => ({
    ...state,
    [payload.resource]: {
      ...state[payload.resource],
      loading: true,
      error: false
    }
  }))
  .handleAction(Actions.setResourcesFailure, (state, { payload }) => ({
    ...state,
    [payload.resource]: {
      ...state[payload.resource],
      loading: false,
      error: true
    }
  }))
  .handleAction(
    Actions.setResourcesSuccess,
    (state, { payload: { resource, value } }) => {
      const keyType = findKeyIndex(resource)
      const list = [...state[resource].list]

      // @ts-ignore
      const isPresent = list.findIndex(item => item[keyType] === value[keyType])

      if (isPresent <= -1) {
        // add new
        if (list.length <= PAGE_SIZE_EDIT) {
          return {
            ...state,
            [resource]: {
              ...state[resource],
              loading: false,
              list: [...list, value]
            }
          }
        }
        return {
          ...state,
          [resource]: {
            ...state[resource],
            loading: false
          }
        }
      }

      // update
      return {
        ...state,
        [resource]: {
          ...state[resource],
          loading: false,
          list: list.map((item, index) =>
            index !== isPresent ? item : { ...value }
          )
        }
      }
    }
  )

export default reducer
